<script setup lang="ts">
import type { LocationQueryValue } from '#vue-router'

definePageMeta({
  view: 'cart',
})

const route = useRoute()
const cartStore = useCartStore()
const storefrontStore = useStorefrontStore()
const { currentStorefrontCode } = storeToRefs(storefrontStore)
const itemsQuery = route.query.items

const { upsellDescription, upsellProducts } = useSegmentedUpsell()
// const testLink = http://localhost:3000/uk/checkout/cart?items=7333102442632x1,7333102606874x2,7333102608397x3
// TODO: Add error handling for 404 code: OUT_OF_STOCK
function restoreCart(itemsQuery: LocationQueryValue | LocationQueryValue[] | undefined,
) {
  if (typeof itemsQuery !== 'string')
    return
  cartStore.restoreCartFromQuery(itemsQuery)
}

const subtotalWithItemsLabel = computed(() =>
  cartStore.cartSummary.itemsCount > 1 ? 'subtotalWithItems' : 'subtotalWithItem',
)

onMounted(() => {
  itemsQuery && restoreCart(itemsQuery)
})
</script>

<template>
  <section
    v-if="!cartStore.state.items.length"
    class="empty"
    data-test="cart-empty-statement"
  >
    <p>{{ $t('cartIsEmpty') }}</p>
  </section>

  <section
    v-else
    class="cart-view"
  >
    <CartItems class="items" />

    <CheckoutSectionLayout>
      <CheckoutDiscountCode />
    </CheckoutSectionLayout>

    <div class="line" />

    <CheckoutShippingBar
      v-if="cartStore.state.items.length"
      class="shipping-bar-container"
    />

    <div class="summary">
      <div class="summary-row subtotal">
        <span data-test="subtotal-text">
          {{ $t(subtotalWithItemsLabel, { amount: cartStore.cartSummary.itemsCount.toString() }) }}
        </span>
        <span data-test="subtotal-price">
          {{ $price(cartStore.cartSummary.priceProducts, undefined, false) }}
        </span>
      </div>
      <div
        v-if="cartStore.cartSummary.priceDiscount"
        class="summary-row discount-total"
      >
        <span>{{ $t('discount') }}</span>
        <span>- {{ $price(cartStore.cartSummary.priceDiscount, undefined, false) }}</span>
      </div>
      <div class="summary-row total">
        <span>{{ $t('totalSum') }}</span>
        <span>{{ $price(cartStore.cartSummary.priceTotal, undefined, false) }}</span>
      </div>
    </div>

    <div class="line" />

    <CheckoutUpsell
      v-if="upsellProducts && upsellProducts.length > 0"
      :upsell-description="upsellDescription"
      :upsell-products="upsellProducts"
      page-source="cartPageUpsell"
    />

    <CheckoutAdyenExpressPayment />
    <ButtonBasic
      tag="nuxt-link"
      class="checkout-btn"
      design="radius-without-border"
      data-test="checkout-button"
      :uppercase="true"
      :bold="true"
      background-color="var(--green)"
      hover-color="var(--green-hover)"
      :to="$checkoutUrl({ route, view: 'address' })"
    >
      <span class="normal">{{ $t('checkout') }}</span>
    </ButtonBasic>
  </section>
</template>

<style lang="scss" scoped>
@import 'assets/scss/rules/breakpoints';
@import 'assets/scss/typography/body';

.empty {
  height: 100%;
  display: grid;
  place-items: center;
  flex-grow: 1;
}
.cart-view {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding-top: 1.6rem;
  padding-bottom: 2.4rem;

  @media screen and (min-width: $phone) {
    padding: 1.6rem calc(10% + 1.5rem) 2.4rem;
  }
  @media screen and (min-width: $phablet) {
    padding: 1.6rem calc(20% + 1.5rem) 2.4rem;
  }
  @media screen and (min-width: $desktop) {
    padding: 1.6rem calc(25% + 1.5rem) 2.4rem;
  }
}

.items {
  padding: 2rem 1.6rem 1rem;

  @media screen and (min-width: $phablet) {
    padding: 2rem 0;
  }

  @media screen and (min-width: $desktop) {
    padding: 2rem 0 1rem;
  }
}
.discount-code {
  min-height: min-content;
}

.line {
  border-bottom: 1px solid var(--pink);
  margin: 0 1.6rem;

  @media screen and (min-width: $phablet) {
    margin: 0;
  }
}

.shipping-bar-container {
  padding: 0 1.6rem;
  margin: 3.2rem 0;

  @media screen and (min-width: $phablet) {
    padding: 0;
  }
}

.summary {
  padding: 2.4rem 1.6rem;
  margin-bottom: 1.6rem;

  @media screen and (min-width: $phablet) {
    padding: 2.4rem 0;
  }
}

.summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;

  + div {
    margin-top: 1rem;
  }

  span {
    white-space: nowrap;
  }
}

.discount-total {
  color: var(--green);
}

.checkout-btn {
  margin: 2rem 1.6rem 4.4rem;
  @media screen and (min-width: $phablet) {
    margin: 2rem 0 4.4rem;
  }
  min-height: 4.8rem;
}
</style>
